import React from 'react';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { LanguageTokenType } from 'src/domains/layouts/state/languagesState/LanguagesUtils';
import { RealityCheckPopupState } from 'src/domains/layouts/state/popups/RealityCheckPopupState';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import {
    ButtonWrapper,
    Description,
    FakeLink,
    PopupContent,
    PopupHeader,
    RealityCheckPopupOverlay,
    RealityCheckPopupWrapper,
    TimerDescription,
} from 'src/domains/layouts/webview/components/popups/realityCheckPopup/RealityCheckPopup.style';
import { RealityCheckTimer } from 'src/domains/layouts/webview/components/popups/realityCheckPopup/RealityCheckTimer';
import { observer } from 'src/utils/mobx-react';
import { AppLayoutsState } from 'src/domains/layouts/state/AppLayouts.state';
import { StarLoginState } from 'src/domains/players/state/starLoginState/StarLoginState';

interface ResponsibleGamblingType {
    onClick: () => void;
    state: RealityCheckPopupState;
}

interface AccountHistoryType {
    onClick: () => void;
}

const ResponsibleGambling = observer('ResponsibleGambling', ({ onClick, state }: ResponsibleGamblingType) => {
    const common = useCommon();
    const languagesState = LanguagesState.get(common);
    const { config } = ConfigComponents.get(common);

    const realityCheckTime = state.activeRealityCheckFrequencyForView ?? '';

    if (config.userCanChangeRealityCheck) {
        return languagesState.translateTokens(
            languagesState.getTranslation(
                'reality-check.popup.description',
                'You have requested a reality check every [time] minutes, which can be updated [here]'
            ),

            (singleParam: LanguageTokenType) => {
                if (singleParam.tag === 'here') {
                    return <FakeLink onClick={onClick}>here</FakeLink>;
                }
                if (singleParam.tag === 'time') {
                    return <span>{realityCheckTime}</span>;
                }
            }
        );
    }

    return languagesState.translateTokens(
        languagesState.getTranslation(
            'reality-check.popup.description-without-reality-check',
            'You have requested a reality check every [time] minutes.'
        ),

        (singleParam: LanguageTokenType) => {
            if (singleParam.tag === 'time') {
                return <span>{realityCheckTime}</span>;
            }
        }
    );
});

const AccountHistory = observer('AccountHistory', ({ onClick }: AccountHistoryType) => {
    const common = useCommon();
    const languagesState = LanguagesState.get(common);

    const text = languagesState.translateTokens(
        languagesState.getTranslation(
            'reality-check.popup.account-history',
            'Your account history can be found [here]'
        ),

        (singleParam: LanguageTokenType) => {
            if (singleParam.tag === 'here') {
                return <FakeLink onClick={onClick}>here</FakeLink>;
            }
        }
    );

    return text;
});

export const RealityCheckPopup = observer('RealityCheckPopup', (): JSX.Element | null => {
    const common = useCommon();
    const loginState = StarLoginState.get(common);

    const { realityCheckPopupState } = AppLayoutsState.get(common);

    return (
        <RealityCheckPopupOverlay>
            <RealityCheckPopupWrapper>
                <PopupContent>
                    <PopupHeader>
                        <I18n
                            langKey='reality-check.popup.header'
                            defaultText='Reality check'
                        />
                    </PopupHeader>

                    <TimerDescription>
                        <I18n
                            langKey='reality-check.popup.header.description'
                            defaultText='Your session has exceeded:'
                        />
                    </TimerDescription>

                    <RealityCheckTimer state={realityCheckPopupState} />

                    <Description>
                        <ResponsibleGambling
                            onClick={realityCheckPopupState.handleRedirectToGamblingLimits}
                            state={realityCheckPopupState}
                        />
                    </Description>
                    <Description>
                        <AccountHistory onClick={realityCheckPopupState.handleRedirectToTransactionHistory} />
                    </Description>
                </PopupContent>

                <footer>
                    <ButtonWrapper
                        onClick={loginState.logOut}
                        size='medium'
                        version='primary'
                    >
                        <I18n
                            langKey='reality-check.popup.log-out'
                            defaultText='Log Out'
                        />
                    </ButtonWrapper>

                    <ButtonWrapper
                        onClick={realityCheckPopupState.handleContinue}
                        size='medium'
                        version='secondary-light'
                    >
                        <I18n
                            langKey='reality-check.popup.continue'
                            defaultText='Continue'
                        />
                    </ButtonWrapper>
                </footer>
            </RealityCheckPopupWrapper>
        </RealityCheckPopupOverlay>
    );
});
